<template>
    <div class="index" ref="indexElement" />
    <div class="itemlist scrollable" ref="itemListElement" />
    <ScrollTopButton @click="onScrollTopButtonClick" />
</template>

<script setup lang="ts">
    import { TastierClient } from "@/app/TastierClient";
    import { CategorySlider } from "@/app/elements/CategorySlider";
    import { trans } from "@/app/localization/Trans";
    import { Helper } from "@/app/ui/Helper";
    import type { ICustomerArticle, ICustomerCategory } from "@/data/model/Customer";
    import { Format } from "@/data/util/Format";
    import { clientLanguage } from "@/shared/localization/language";
    import { DomUtil } from "@/shared/ui/Helper";
    import { Image } from "@/shared/ui/Image";
    import { Ripple } from "@/shared/ui/Ripple";
    import { onMounted, ref } from "vue";

    import ScrollTopButton from "@/app/components/elements/ScrollTopButton.vue";

    // html refs
    const indexElement = ref<HTMLElement | null>(null);
    const itemListElement = ref<HTMLElement | null>(null);

    let $itemList: JQuery;
    let $index: JQuery;
    let categorySlider: CategorySlider;
    let isScrolled = false;

    let listenToScrollEvents = true;

    const emit = defineEmits<{
        (e: "articleClick", article: ICustomerArticle): void;
        (e: "scrolledChanged", isScrolled: boolean): void;
    }>();

    onMounted(() => {
        initialize();
    });

    function initialize() {
        $index = $(indexElement.value!);
        $itemList = $(itemListElement.value!);

        categorySlider = new CategorySlider().appendTo($index);
        categorySlider.onSelected = (scrollToId) => {
            const cat = $itemList.find('> .section[data-id="' + scrollToId + '"]');
            if (cat.length === 0) {
                return;
            }

            listenToScrollEvents = false;
            const elementPosition = cat.position().top + $itemList.scrollTop();
            const paddingOffset = elementPosition > 131 ? 47 : 131;
            const elementOffset = elementPosition - paddingOffset;
            checkScrollCollapse(elementOffset);

            DomUtil.scrollTo($itemList, elementOffset, () => {
                listenToScrollEvents = true;
            });
        };

        $itemList.scroll(() => {
            onScroll();
        });
    }

    function redrawList() {
        $itemList.empty();

        if (
            TastierClient.context.currentOffer === undefined ||
            TastierClient.context.currentOffer.entries.length === 0
        ) {
            $itemList.append('<div class="nodata"><div class="lbl">' + trans.order.noOffer + "</div></div>");
            categorySlider.setCategories([]);
            $itemList.scrollTop(0);
            updateCart();
            return;
        }

        const itemCategories = TastierClient.context.currentOffer.entries;
        for (const itemCategory of itemCategories) {
            renderSection(itemCategory).appendTo($itemList);
        }

        categorySlider
            .setCategories(
                itemCategories.map((c) => {
                    return {
                        id: c.id,
                        name: Format.translate(c.name, clientLanguage()),
                    };
                })
            )
            .moveToFirst();

        updateCart();
        onScroll();
    }

    function renderSection(model: ICustomerCategory): JQuery {
        const section = $(`<div class="section" data-id="${model.id}" />`);

        const header = $('<div class="header" />').appendTo(section);
        if (model.imagePath !== undefined) {
            header
                .addClass("withimage")
                .append('<div class="background" style="background-image:url(' + model.imagePath + ');"></div>')
                .append('<div class="blackdim" />');
        } else header.addClass("nameonly");

        const name = $('<div class="name"><h2>' + Format.htmlEscape(Format.translate(model.name, clientLanguage())) + "</h2></div>"
        ).appendTo(header);
        const desc = Format.translate(model.description, clientLanguage());
        if (desc !== undefined)
            name.addClass("twolines").append('<div class="description">' + Format.htmlEscape(desc) + "</div>");

        const articles = $('<div class="articles" />').appendTo(section);

        if (model.articles.find((c) => c.imagePath !== undefined) !== undefined) articles.addClass("withimage");

        for (const article of model.articles) {
            renderArticle(article).appendTo(articles);
        }

        return section;
    }

    function renderArticle(model: ICustomerArticle): JQuery {
        const article = $('<div class="article" />').data("model", model);

        const nameStr = Format.htmlEscape(Format.translate(model.name, clientLanguage()));
        const name = $('<div class="name"><h3>' + nameStr + "</h3></div>").appendTo(article);
        const desc = Format.translate(model.shortDescription, clientLanguage());
        if (desc !== undefined)
            name.addClass("twolines").append('<div class="description">' + Format.htmlEscape(desc) + "</div>");

        if (model.isOpenPriceEnabled === false)
            article.append('<div class="price">' + Helper.formatPrice(model) + "</div>");

        if (model.imagePath !== undefined) {
            article.addClass("hasimage");

            const imgElement = $(
                `<div class="image"><img src="${model.imagePath}" alt="${nameStr}" data-original-img="${model.originalImagePath}" /></div>`
            );

            imgElement.click((e) => {
                if (model?.originalImagePath === undefined) return;

                e.stopPropagation();
                showImage(model.originalImagePath);
            });

            article.append(imgElement);
        }

        article.append('<div class="line" />');

        Ripple.bind(article);

        article.click((e) => {
            const article = $(e.currentTarget).data("model");
            emit("articleClick", article);
        });

        return article;
    }

    function updateCart() {
        const offer = TastierClient.context.currentOffer;

        $itemList.find("> .section > .articles > .article").each((i, e) => {
            const articleEl = $(e);
            const article: ICustomerArticle = articleEl.data("model");

            const cartEntries =
                offer === undefined || offer.cart === undefined
                    ? []
                    : offer.cart.items.filter((c) => c.articleId === article.articleId);

            if (cartEntries.length === 0) {
                if (articleEl.hasClass("added")) {
                    articleEl
                        .removeClass("added")
                        .delay(400)
                        .queue((a) => {
                            articleEl.find("> .name > .qty").remove();
                            a();
                        });
                }
            } else {
                let qtyContainer = articleEl.find("> .name > .qty");
                if (qtyContainer.length === 0) {
                    qtyContainer = $('<div class="qty" />').prependTo(articleEl.find("> .name"));
                    qtyContainer[0].offsetHeight;
                }

                let sum = 0;
                for (const cartItem of cartEntries) {
                    sum += cartItem.quantity;
                }

                qtyContainer.html(sum + "x");
                articleEl.addClass("added");
            }
        });
    }

    function onScrollTopButtonClick() {
        listenToScrollEvents = false;
        categorySlider!.moveToFirst();

        checkScrollCollapse(0);
        DomUtil.scrollTo($itemList, 0, () => {
            listenToScrollEvents = true;
        });
    }

    function onScroll() {
        if (!listenToScrollEvents) return;

        const currentOffset = $itemList.scrollTop();

        //get section closest to top
        const sections = new Array<{ el: JQuery; offset: number }>();
        $itemList.children(".section").each((i, e) => {
            const el = $(e);
            const offset = el.position().top;
            if (offset <= (isScrolled ? 62 : 131)) sections.push({ el: el, offset: offset });
        });

        if (sections.length > 0) {
            const closest = sections.sort((a, b) => Math.abs(a.offset) - Math.abs(b.offset))[0];
            categorySlider.moveScrollTo(closest.el.attr("data-id"));
        }

        checkScrollCollapse(currentOffset);
    }

    function checkScrollCollapse(forOffset: number) {
        const shouldCollapse = forOffset > 80 && $itemList.height() + 52 < forOffset;

        if (!isScrolled && shouldCollapse) {
            emit("scrolledChanged", true);
            isScrolled = true;
        } else if (isScrolled && !shouldCollapse) {
            emit("scrolledChanged", false);
            isScrolled = false;
        }
    }

    function setIsLoading() {
        $itemList.html('<div class="loading"><div class="loader"></div>' + trans.order.loadingOffer + "</div>");
    }

    async function showImage(imagePath: string) {
        // todo: stolen from CovidController - refactor to a shared function
        const parent = $(".app");

        const dim = $('<div class="dim" />').appendTo(parent);

        dim[0].offsetHeight;
        dim.addClass("shown");

        const pop = $('<div class="identitydocmodal"></div>').appendTo(parent);
        const s = await Image.preloadImage(imagePath);
        let img: JQuery;
        if (s !== undefined) img = $(s).appendTo(pop);
        else img = $('<img src="' + imagePath + '" />').appendTo(pop);

        pop[0].offsetHeight;

        const imgElement = img[0] as HTMLImageElement;
        const imgHeight = (parent.width() / imgElement.width) * imgElement.height;
        pop.css({
            height: imgHeight,
            "margin-top": imgHeight / -2,
        });

        pop.addClass("shown");
        pop.append('<div class="close">' + trans.general.close + "</div>");

        const hide = () => {
            dim.removeClass("shown");
            pop.removeClass("shown")
                .delay(400)
                .queue((e) => {
                    dim.remove();
                    pop.remove();

                    e();
                });
        };

        dim.click(() => {
            hide();
        });

        pop.click(() => {
            hide();
        });
    }

    function reset() {
        $itemList.empty();
        categorySlider.setCategories([]);
    }

    defineExpose({
        redrawList,
        updateCart,
        setIsLoading,
        reset,
    });
</script>
